<template>
  <a-modal
      :visible="visible"
      :width="1200"
      :destroy-on-close="true"
      :footer="null"
      :mask-closable="false"
      :title="title"
      @cancel="handleClose"
  >
    <!-- ============ 筛选 ============ -->
    <div class="flow-filter">
      <a-range-picker
          v-model="timeRange"
          :disabled-date="disabledDate"
          style="width: 240px;"
          value-format="YYYY-MM-DD"
          @change="onSearch"
      />
      <a-select
          v-model="filterForm.status"
          allow-clear
          placeholder="订单状态"
          style="width: 130px;"
          @change="onSearch"
      >
        <a-select-option :value="0">待归集</a-select-option>
        <a-select-option :value="1">待分润</a-select-option>
        <a-select-option :value="2">已完成</a-select-option>
        <a-select-option :value="3">已作废</a-select-option>
      </a-select>
      <a-button type="primary" @click="onSearch">查询</a-button>
      <a-button
          v-if="$authority('merchantStatExport')"
          icon="download"
          type="link"
          @click="onExport"
      >导出 Excel</a-button>
    </div>

    <!-- ============ 表格 ============ -->
    <a-table
        :columns="columns"
        :data-source="flowList"
        :loading="loading"
        :pagination="pagination"
        :row-key="(record) => record.orderNo"
        :scroll="{ x: 1400, y: 500 }"
        bordered
        size="small"
        @change="onTableChange"
    >
      <template slot="totalAmount" slot-scope="text">
        <Balance :amount="text || 0"/>
      </template>
      <template slot="serviceFee" slot-scope="text">
        <Balance :amount="text || 0"/>
      </template>
      <template slot="profitShare" slot-scope="text">
        <Balance :amount="text || 0"/>
      </template>
      <template slot="payType" slot-scope="text">
        <a-tag v-if="text === 'TAX_PLAN'" color="blue">税筹支付</a-tag>
        <a-tag v-else-if="text === 'BALANCE'">余额支付</a-tag>
        <span v-else>-</span>
      </template>
      <template slot="status" slot-scope="text">
        <a-tag v-if="text === 0" color="orange">待归集</a-tag>
        <a-tag v-else-if="text === 1" color="blue">待分润</a-tag>
        <a-tag v-else-if="text === 2" color="green">已完成</a-tag>
        <a-tag v-else-if="text === 3" color="red">已作废</a-tag>
        <span v-else>-</span>
      </template>
      <template slot="paymentStatus" slot-scope="text">
        <a-tag v-if="text === 0">待处理</a-tag>
        <a-tag v-else-if="text === 1" color="blue">处理中</a-tag>
        <a-tag v-else-if="text === 2" color="green">成功</a-tag>
        <a-tag v-else-if="text === 3" color="red">失败</a-tag>
        <span v-else>-</span>
      </template>
      <template slot="orderTime" slot-scope="text">
        {{ text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-' }}
      </template>
    </a-table>
  </a-modal>
</template>

<script>
import dayjs from 'dayjs';
import {message} from 'ant-design-vue';
import Balance from '@/view/components/Balance.vue';

export default {
  name: 'MerchantFlowModal',
  components: {Balance},
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    merchantNo: {
      type: String,
      default: '',
    },
    timeRange: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dayjs,
      filterForm: {
        status: undefined,
      },
      flowList: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
      },
      columns: [
        {title: '订单号', dataIndex: 'orderNo', width: 170, fixed: 'left'},
        {title: '下单时间', dataIndex: 'orderTime', width: 150, scopedSlots: {customRender: 'orderTime'}},
        {title: '支付类型', dataIndex: 'payType', width: 100, align: 'center', scopedSlots: {customRender: 'payType'}},
        {title: '订单金额', dataIndex: 'totalAmount', width: 130, align: 'right', scopedSlots: {customRender: 'totalAmount'}},
        {title: '服务费', dataIndex: 'serviceFee', width: 130, align: 'right', scopedSlots: {customRender: 'serviceFee'}},
        {title: '分润', dataIndex: 'profitShare', width: 130, align: 'right', scopedSlots: {customRender: 'profitShare'}},
        {title: '订单状态', dataIndex: 'status', width: 100, align: 'center', scopedSlots: {customRender: 'status'}},
        {title: '支付状态', dataIndex: 'paymentStatus', width: 100, align: 'center', scopedSlots: {customRender: 'paymentStatus'}},
        {title: '付款账户', dataIndex: 'accountNo', width: 140},
        {title: '收款户名', dataIndex: 'oppAccountName', width: 140},
        {title: '收款账号', dataIndex: 'oppAccountNo', width: 160},
        {title: '收款行名', dataIndex: 'oppBankName', width: 140, ellipsis: true},
        {title: '摘要', dataIndex: 'remark', width: 160, ellipsis: true},
      ],
    };
  },
  computed: {
    title() {
      return this.merchantNo
          ? `商户流水明细 - ${this.merchantNo}`
          : '商户流水明细';
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.merchantNo) {
        // 接收父组件传过来的 timeRange 作为初始值
        this.pagination.current = 1;
        this.loadFlow();
      }
    },
  },
  methods: {
    /**
     * 加载流水
     */
    async loadFlow() {
      if (!this.merchantNo) return;
      this.loading = true;
      try {
        const req = {
          merchantNo: this.merchantNo,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          status: this.filterForm.status || undefined,
          pageNo: this.pagination.current,
          pageSize: this.pagination.pageSize,
        };
        const res = await this.api.getMerchantStatFlow(req);
        if (res.code === 200) {
          this.flowList = res.data.records || [];
          this.pagination.total = res.data.total || 0;
        } else {
          message.error(res.info || '获取流水失败');
          this.flowList = [];
        }
      } catch (e) {
        console.error('加载流水异常', e);
        message.error('加载流水失败');
        this.flowList = [];
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.pagination.current = 1;
      this.loadFlow();
    },

    onTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadFlow();
    },

    /**
     * 导出 Excel
     */
    async onExport() {
      if (!this.flowList.length) {
        message.warning('暂无可导出数据');
        return;
      }
      try {
        const xlsx = await import('xlsx');
        const {utils, writeFile} = xlsx;

        const rows = [
          [`商户编号：${this.merchantNo}`, `时间：${this.timeRange[0]} ~ ${this.timeRange[1]}`],
          [],
          [
            '订单号', '下单时间', '支付类型', '订单金额', '服务费', '分润',
            '订单状态', '支付状态', '付款账户', '付款户名',
            '收款户名', '收款账号', '收款行号', '收款行名', '摘要',
          ],
          ...this.flowList.map((row) => [
            row.orderNo,
            row.orderTime ? dayjs(row.orderTime).format('YYYY-MM-DD HH:mm:ss') : '',
            row.payTypeName || row.payType || '',
            row.totalAmount || 0,
            row.serviceFee || 0,
            row.profitShare || 0,
            row.statusName || '',
            row.paymentStatusName || '',
            row.accountNo || '',
            row.accountName || '',
            row.oppAccountName || '',
            row.oppAccountNo || '',
            row.oppBankNo || '',
            row.oppBankName || '',
            row.remark || '',
          ]),
        ];

        const ws = utils.aoa_to_sheet(rows);
        ws['!cols'] = [
          {wch: 18}, {wch: 18}, {wch: 12}, {wch: 14}, {wch: 12}, {wch: 12},
          {wch: 12}, {wch: 12}, {wch: 16}, {wch: 14},
          {wch: 14}, {wch: 18}, {wch: 14}, {wch: 16}, {wch: 18},
        ];
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, '流水明细');

        const filename = `流水明细-${this.merchantNo}-${this.timeRange[0]}-${this.timeRange[1]}.xlsx`;
        writeFile(wb, filename);
        message.success('导出成功');
      } catch (e) {
        console.error('导出异常', e);
        message.error('导出失败');
      }
    },

    /**
     * 禁用未来日期
     */
    disabledDate(current) {
      return current && current > dayjs().endOf('day');
    },

    /**
     * 关闭
     */
    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="less" scoped>
.flow-filter {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
</style>